import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../ui/button/button";
import { Menu, X, Zap, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const demos = [
  // { label: "COQ9: Lipid Shuttle Mechanism", href: "/demo/coq9" },
  // { label: "TMEM126A: Resolving a Conflict", href: "/demo/tmem" },
  // { label: "ANKZF1 — Resolving a Translation Stall", href: "/demo/ankzf1" },
  // {
  //   label: "PINK1-Parkin — Resolving a Reporter Conflict",
  //   href: "/demo/pink1",
  // },
  // { label: "mtDNA Replication — Three Competing Models", href: "/demo/mtdna" },
  { label: "POLG Clinical — GPRA in Action", href: "/demo/polg-clinical" },
  { label: "KSS Clinical — GPRA", href: "/demo/kss-clinical" },
  { label: "MELAS Clinical — GPRA", href: "/demo/melas-clinical" },
  {
    label: "CPEO-plus — Scoring Under Uncertainty",
    href: "/demo/cpeo-clinical",
  },
];

function NavBar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [{ label: "Features", href: "#features" }];

  return (
    <motion.nav
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background/80 backdrop-blur-xl border-b border-border/50"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group">
          <div className="relative w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center group-hover:bg-primary/30 transition-colors">
            <Zap className="w-4 h-4 text-primary" />
            <div className="absolute inset-0 rounded-lg bg-primary/10 animate-glow-pulse" />
          </div>
          <span className="text-base md:text-lg font-bold tracking-tight">
            Mito <span className="text-primary"> Agent </span>
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {link.label}
            </a>
          ))}

          {/* Demo dropdown */}
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDemoOpen(!demoOpen);
              }}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Demos
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${
                  demoOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            <AnimatePresence>
              {demoOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full left-0 mt-2 rounded-xl border border-border/50 bg-background/95 backdrop-blur-xl shadow-xl z-[9999]"
                  style={{ width: "400px" }}
                >
                  {demos.map((demo) => (
                    <Link
                      key={demo.label}
                      to={demo.href}
                      onClick={() => setDemoOpen(false)}
                      className="block px-4 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors border-b border-border/30 last:border-0 whitespace-nowrap"
                    >
                      {demo.label}
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <Link to="/playground">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-5">
              Try Playground
            </Button>
          </Link>
        </div>

        <button
          className="md:hidden text-foreground"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? (
            <X className="w-5 h-5" />
          ) : (
            <Menu className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden bg-background/95 backdrop-blur-xl border-b border-border overflow-hidden"
          >
            <div className="px-6 py-4 space-y-4">
              <a
                href="#features"
                className="block text-sm text-muted-foreground hover:text-foreground"
                onClick={() => setMobileOpen(false)}
              >
                Features
              </a>

              {/* Mobile demos list */}
              <div>
                <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-3">
                  Demos
                </p>
                {demos.map((demo) => (
                  <Link
                    key={demo.label}
                    to={demo.href}
                    className="block text-sm text-muted-foreground hover:text-foreground py-2 border-b border-border/30 last:border-0"
                    onClick={() => setMobileOpen(false)}
                  >
                    {demo.label}
                  </Link>
                ))}
              </div>

              <Link to="/playground" onClick={() => setMobileOpen(false)}>
                <Button className="w-full bg-primary text-primary-foreground">
                  Try Playground
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}

export default NavBar;
